/**
 * Hook for fetching lead module tab counts (All Leads).
 * Provides counts for the leads module landing page tabs.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/api/api-client';
import { useOrganizationStore } from '@/store/organization.store';

export interface LeadModuleCounts {
  allLeads: number;
}

interface UseLeadModuleCountsOptions {
  enabled?: boolean;
}

// Query key for lead module counts
const leadModuleCountsKey = ['lead-module-counts'] as const;

/**
 * Fetches lead module counts
 */
async function fetchLeadModuleCounts(): Promise<LeadModuleCounts> {
  return apiClient<LeadModuleCounts>('/leads/module-counts');
}

/**
 * Hook for fetching lead module counts.
 * Returns count for "All Leads".
 */
export function useLeadModuleCounts({ enabled = true }: UseLeadModuleCountsOptions = {}) {
  const queryClient = useQueryClient();
  const currentOrganization = useOrganizationStore((state) => state.currentOrganization);

  const { data, isLoading, error } = useQuery({
    queryKey: leadModuleCountsKey,
    queryFn: fetchLeadModuleCounts,
    enabled: enabled && !!currentOrganization?.id,
    staleTime: 30000, // Consider data fresh for 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });

  const counts = {
    allLeads: data?.allLeads,
  };

  /**
   * Invalidate and refetch counts
   */
  const refetchCounts = () => {
    queryClient.invalidateQueries({ queryKey: leadModuleCountsKey });
  };

  return {
    counts,
    isLoading,
    error,
    refetchCounts,
  };
}

// Export query key for external use
export { leadModuleCountsKey };
